/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Col,
  Form,
  FormInstance,
  Modal,
  Row,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import classNames from 'classnames';
import { Header } from 'components/header';
import { StarIcon, TrashIcon } from 'components/icons';
import { LevelsCollapse } from 'components/levels-collapse';
import { Select, SelectOption } from 'components/select';
import { useEmployee } from 'hooks';
import { useSaveTechnologies } from 'hooks/use-save-technologies';
import { useSkillLevelDictionary } from 'hooks/use-skill-levels-dictionary';
import { useTechnologiesDictionary } from 'hooks/use-technologies-dictionary';
import { FieldData, NamePath } from 'rc-field-form/lib/interface';
import { EpTechnologySkill } from 'types/graphql-types';
import styles from './styles.less';

export type TechnologiesFormProps = {
  editMode?: boolean;
  setEditMode?: (value: boolean) => void;
  formName?: string;
  form?: FormInstance;
};

const HORIZONTAL_GAP = 0;
const VERTICAL_GAP = 6;

type FormValues = {
  emsPrimaryLevel: string;
  levels: Record<string, string[]>;
};

const renderEMSPrimaryTech = (
  editMode: boolean,
  tech: EpTechnologySkill | undefined,
  options: SelectOption[],
): React.ReactNode => (
  <Row
    gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}
    className={classNames(styles['row'], styles['data-row'])}
  >
    <Col span={16}>
      <Tooltip
        placement="right"
        title="This is your primary technology from EMS. You can't update it or delete"
      >
        {tech ? (
          <Tag className={styles['ems-primary']}>
            <StarIcon />
            {tech.name}
          </Tag>
        ) : (
          '—'
        )}
      </Tooltip>
    </Col>
    <Col span={8}>
      {tech ? (
        editMode ? (
          <Form.Item
            name={'emsPrimaryLevel'}
            className={styles['form-item-level']}
          >
            <Select options={options} placeholder={'Choose the skill level'} />
          </Form.Item>
        ) : (
          <div className={styles['level']}>{tech.level}</div>
        )
      ) : (
        '—'
      )}
    </Col>
  </Row>
);

const renderEmptyRow = (): ReactNode => (
  <Row
    key="empty-row"
    className={classNames(styles['row'], styles['data-row'])}
  >
    <Col span={16}>—</Col>
    <Col span={8}>—</Col>
  </Row>
);

const renderTechTags = (
  techs: EpTechnologySkill[],
  level: string,
): React.ReactNode => (
  <Row key={level} className={classNames(styles['row'], styles['data-row'])}>
    <Col span={16}>
      <Row gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}>
        {techs.map((tech) =>
          tech.desc === 'EP' ? (
            <Col key={tech.name}>
              <Tag>{tech.name}</Tag>
            </Col>
          ) : (
            <Col key={tech.name}>
              <Tooltip
                key={tech.name}
                placement="bottom"
                title={
                  'You added this skill yourself. So far, only you can see this skill.'
                }
              >
                <Tag className={styles['custom-tech']}>{tech.name}</Tag>
              </Tooltip>
            </Col>
          ),
        )}
      </Row>
    </Col>
    <Col span={8}>
      <div className={styles['level']}>{level}</div>
    </Col>
  </Row>
);

type TechFieldProps = {
  level: string;
  techOptions: SelectOption[];
  onDelete: () => void;
  onChange: (values: string[], name: NamePath) => void;
  dictionary: string[];
};

const TechField: React.FC<TechFieldProps> = ({
  level,
  techOptions,
  onChange,
  onDelete,
  dictionary,
}) => (
  <Row
    gutter={[HORIZONTAL_GAP, VERTICAL_GAP]}
    className={classNames(styles['row'], styles['data-row'])}
  >
    <Col span={16}>
      <Form.Item
        name={['levels', level]}
        className={styles['form-item']}
        rules={[
          {
            validator: (_rule: unknown, value: string[], callback) => {
              if (
                value.find(
                  (str) => str.length > 30 && !dictionary.includes(str),
                )
              ) {
                callback('Technology should not exceed 30 characters');
              }

              callback();
            },
          },
        ]}
      >
        <Select
          options={techOptions}
          mode={'tags'}
          onChange={(value) => onChange(value as string[], ['levels', level])}
          placeholder={`Choose your ${level} level skills`}
        />
      </Form.Item>
    </Col>
    <Col span={8}>
      <div className={styles['level']}>
        {level}
        <TrashIcon onClick={onDelete} />
      </div>
    </Col>
  </Row>
);

type PrimariesProps = {
  editMode: boolean;
  emsPrimaryTechnology?: EpTechnologySkill;
};

const Primaries: FC<PrimariesProps> = ({ editMode, emsPrimaryTechnology }) => {
  const { t } = useTranslation();

  const { data: levelData } = useSkillLevelDictionary();
  const levels = levelData?.skillLevelList?.slice().reverse() || [];

  const levelOptions = levels.map((level) => ({
    label: level,
    value: level,
  }));

  return (
    <>
      <Row
        className={classNames(styles['row'], styles['header-row'])}
        align="middle"
      >
        <Col span={16}>
          <Header level={5} className={styles['header']}>
            {t('Primary technology')}
          </Header>
        </Col>
        <Col span={8}>
          <Header level={5} className={styles['header']}>
            {t('Experience')}
          </Header>
        </Col>
      </Row>
      <>{renderEMSPrimaryTech(editMode, emsPrimaryTechnology, levelOptions)}</>
    </>
  );
};

export const TechnologiesForm: FC<TechnologiesFormProps> = (props) => {
  const { editMode = false, formName, form, setEditMode } = props;

  const { t } = useTranslation();

  const { employee, error } = useEmployee();

  const { data: levelData } = useSkillLevelDictionary();
  const levels = levelData?.skillLevelList?.slice().reverse() || [];

  const { data: techData } = useTechnologiesDictionary();
  const techList = techData?.technologyList || [];

  const [unavailableTechs, setUnavailableTechs] = useState(
    employee?.technologies
      ?.filter((tech) => tech.desc !== 'PRIMARY_EMS')
      .map((tech) => tech.name) || [],
  );

  const recalculateAvailableTechs = useCallback((): void => {
    const values = form?.getFieldsValue() as Partial<FormValues>;
    const takenTechs = Object.values(values.levels || {}).flat();

    setUnavailableTechs(takenTechs);
  }, [form]);

  const techOptions = techList
    .filter(
      (tech) =>
        !unavailableTechs.find((x) => x.toUpperCase() === tech.toUpperCase()),
    )
    .map((tech) => ({
      label: tech,
      value: tech,
    }));

  const saveTechnologies = useSaveTechnologies();

  if (!employee || error) {
    return <Alert message={error?.message} showIcon type="error"></Alert>;
  }

  const techs =
    employee.technologies?.filter(
      (tech) => tech.desc === 'EP' || tech.desc === employee.login,
    ) || [];

  const emsPrimaryTechnology = employee.technologies?.find(
    (tech) => tech.desc === 'PRIMARY_EMS',
  );

  const techsInitialValues: Record<string, string[]> = levels.reduce(
    (acc, level) => ({
      ...acc,
      [level]: techs
        .filter((tech) => tech.level === level)
        .map((tech) => tech.name),
    }),
    {} as Record<string, string[]>,
  );

  const initialValues: Partial<FormValues> = {
    emsPrimaryLevel: emsPrimaryTechnology?.level,
    levels: techsInitialValues,
  };

  const onSave = (values: Partial<FormValues>): void => {
    const techsToSave: EpTechnologySkill[] = [];

    Object.entries(values?.levels || []).forEach(([key, value]) => {
      techsToSave.push(
        ...value.map((tech) => ({
          desc: techList.find((x) => x === tech) ? 'EP' : employee.login,
          level: key,
          name: tech,
        })),
      );
    });
    const emsPrimaryLevel = values.emsPrimaryLevel;

    if (emsPrimaryLevel && emsPrimaryTechnology) {
      techsToSave.unshift({
        desc: 'PRIMARY_EMS',
        level: emsPrimaryLevel,
        name: emsPrimaryTechnology.name,
      });
    }

    void saveTechnologies({ technologies: techsToSave })
      .then(() => {
        notification.success({ message: t('Technologies have been saved!') });
        setEditMode && setEditMode(false);
      })
      .catch(() =>
        notification.error({
          message: t('Something has gone wrong! Try again!'),
        }),
      );
  };

  const onDelete = (level: string): void => {
    Modal.confirm({
      content: t(`Are you sure you want to delete your ${level} level skills?`),
      okText: t('Yes, delete'),
      onOk: () => {
        form?.setFieldValue(['levels', level], []);
        recalculateAvailableTechs();
      },
      title: t('Confirm action'),
    });
  };

  const onMultiselectChange = (
    newFieldValues: string[],
    name: NamePath,
  ): void => {
    const fieldValues = form?.getFieldsValue() as Partial<FormValues>;
    const values = Object.values(fieldValues.levels || []).flat();

    const valuesToDelete: string[] = [];

    newFieldValues
      .slice()
      .reverse()
      .forEach((tech) => {
        if (!valuesToDelete.includes(tech)) {
          const duplicate = values.find(
            (x) =>
              x.toUpperCase() === tech.toUpperCase() &&
              (x !== tech || values.filter((y) => y === x).length > 1),
          );

          if (
            duplicate &&
            !valuesToDelete.find(
              (x) => x.toUpperCase() === duplicate.toUpperCase(),
            )
          ) {
            valuesToDelete.push(tech);
          }
        }
      });
    const newValues = newFieldValues.filter(
      (tech) => !valuesToDelete.includes(tech),
    );

    form?.setFieldValue(name, newValues);
    recalculateAvailableTechs();
  };

  const onFieldsChange = (changedFields: FieldData[]): void => {
    changedFields.forEach((changedField) => {
      const name = changedField.name;

      if (Array.isArray(name) && name[0] === 'epPrimaryTech') {
        recalculateAvailableTechs();
      }
    });
  };

  return (
    <Row>
      <Col xs={{ span: 24 }} lg={{ span: 16 }}>
        <Form
          name={formName}
          form={form}
          initialValues={initialValues}
          onFinish={onSave}
          preserve={false}
          onFieldsChange={onFieldsChange}
        >
          <Primaries
            editMode={editMode}
            emsPrimaryTechnology={emsPrimaryTechnology}
          />
          <Row
            className={classNames(styles['row'], styles['header-row-second'])}
          >
            <Col span={16}>
              <Header level={5} className={styles['header']}>
                {t('Development tools')}
              </Header>
            </Col>
            <Col span={8}>
              <Header level={5} className={styles['header']}>
                {t('Experience')}
              </Header>
            </Col>
          </Row>
          {editMode
            ? levels.map((level) => (
                <TechField
                  key={level}
                  level={level}
                  techOptions={techOptions}
                  onDelete={() => onDelete(level)}
                  onChange={onMultiselectChange}
                  dictionary={techList}
                />
              ))
            : techs.length === 0
            ? renderEmptyRow()
            : levels.map((level) => {
                const filteredTechs = techs.filter(
                  (tech) => tech.level === level,
                );

                return filteredTechs.length > 0
                  ? renderTechTags(filteredTechs, level)
                  : null;
              })}
        </Form>
      </Col>
      <Col xs={{ span: 0 }} lg={{ span: 8 }}>
        <LevelsCollapse openByDefault />
      </Col>
    </Row>
  );
};
